/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type React from "react"
import type { JSX } from "react"
import { useId } from "react"

interface SelectFilterProps<T extends string | number | boolean> {
    options: { label: string; value: T }[]
    value: T | null
    onChange: (value: T | null) => void
    label?: string
    noSelectionLabel?: string
    disabled?: boolean
    wrapperClassName?: string
    selectClassName?: string
}

export function SelectFilter<T extends string | number | boolean>({
    options,
    value,
    onChange,
    label,
    noSelectionLabel = "All",
    disabled = false,
    wrapperClassName = "",
    selectClassName = ""
}: SelectFilterProps<T>): JSX.Element {
    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selected = e.target.value

        if (selected === "null") {
            onChange(null)
            return
        }

        if (selected === "none") {
            onChange(selected as T)
            return
        }

        const firstValue = options[0]?.value
        let converted: T

        if (typeof firstValue === "number") {
            converted = Number(selected) as T
        } else if (typeof firstValue === "boolean") {
            converted = (selected === "true") as T
        } else {
            converted = selected as T
        }

        onChange(converted)
    }

    const id = useId()
    const selectElement = (
        <select
            id={id}
            className={`form-select ${selectClassName}`}
            value={String(value)}
            onChange={handleChange}
            disabled={disabled}
        >
            <option value="null">{noSelectionLabel}</option>
            {options.map((opt) => (
                <option key={String(opt.value)} value={String(opt.value)}>
                    {opt.label}
                </option>
            ))}
        </select>
    )

    // CASE 1: Floating label
    if (label) {
        return (
            <div className={`form-floating ${wrapperClassName}`}>
                {selectElement}
                <label htmlFor={id}>{label}</label>
            </div>
        )
    }

    // CASE 2: No label → no form-floating wrapper
    return <div className={wrapperClassName}>{selectElement}</div>
}
